import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { AppTopBar, formatIDR } from "../components/AppTopBar";
import { useAuth } from "../context/AuthContext";
import { filterDeadstock, downloadDeadstockXlsx } from "../lib/deadstockExport";

const compactNumber = (v) => Number(v || 0).toLocaleString("id-ID");

const fmtDate = (value) => {
  if (!value) return "Belum pernah";
  const p = String(value).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : value;
};

export default function DeadstockDashboard() {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState({ items: [], count: 0, period_days: 30 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exportError, setExportError] = useState("");
  const [onlyAvailable, setOnlyAvailable] = useState(true);

  const load = useCallback(async () => {
    if (!user || user.role !== "OWNER") return;
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/analytics/sku-no-sales?days=${days}`);
      setData(res.data || { items: [], count: 0, period_days: days });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Data deadstock gagal dimuat.");
      setData({ items: [], count: 0, period_days: days });
    } finally {
      setLoading(false);
    }
  }, [user, days]);

  useEffect(() => { load(); }, [load]);

  if (user && user.role !== "OWNER") {
    return <div className="app-shell"><AppTopBar active="analytics" /><main className="content"><div className="alert-error">Akses ditolak.</div></main></div>;
  }

  const allItems = Array.isArray(data.items) ? data.items : [];
  const visible = filterDeadstock(allItems, onlyAvailable);
  const visibleAsset = visible.reduce((sum, item) => sum + Number(item.stock_value || 0), 0);
  // The existing API caps items at 300 while count may be higher.
  // Never label an incomplete XLSX as the complete active-period result.
  const completeResult = data.period_days === days && allItems.length >= Number(data.count ?? allItems.length);
  const exportExcel = () => {
    setExportError("");
    if (loading || !completeResult) {
      setExportError("Export ditahan: API Deadstock belum memuat seluruh SKU periode aktif (maks. 300).");
      return;
    }
    try {
      downloadDeadstockXlsx(visible, days);
    } catch (e) {
      setExportError(e.message || "Export Deadstock gagal.");
    }
  };

  return (
    <div className="app-shell">
      <AppTopBar active="analytics" />
      <main className="content" style={{ paddingTop: 14, width: "100%", maxWidth: "none", paddingLeft: 24, paddingRight: 24 }}>
        <div className="deadstock-container" style={styles.container}>
          <header className="deadstock-header" style={styles.header}>
            <div style={styles.headerMain}>
              <div style={styles.headerIcon}>▣</div>
              <div>
                <div style={styles.titleRow}>
                  <h1 style={styles.title}>Produk Deadstock</h1>
                  <span style={styles.preview}>DEMAND INTELLIGENCE</span>
                </div>
                <p style={styles.subtitle}>SKU ACTIVE dengan penjualan 0 selama periode yang dipilih. Prioritas daftar berdasarkan nilai aset.</p>
              </div>
            </div>
            <Link to="/analytics" style={styles.back}>← &nbsp; Kembali ke Demand Intelligence</Link>
          </header>

          {error && <div style={styles.error}>{error}</div>}
          {exportError && <div style={styles.error} role="alert">{exportError}</div>}

          <section className="deadstock-control" style={styles.controlPanel}>
            <div>
              <div style={styles.label}>PERIODE TIDAK ADA PENJUALAN</div>
              <div className="deadstock-tabs" style={styles.tabs}>
                {[7, 14, 30].map((d) => (
                  <button key={d} type="button" onClick={() => setDays(d)} style={{ ...styles.tab, ...(days === d ? styles.active : {}) }}>
                    {d} Hari
                  </button>
                ))}
              </div>
            </div>
            <div className="deadstock-control-right" style={styles.controlRight}>
              <div style={styles.periodHint}>Data tanpa penjualan</div>
              <button type="button" onClick={load} disabled={loading} style={styles.refresh}>{loading ? "Memuat…" : "↻  Refresh"}</button>
            </div>
          </section>

          <section className="deadstock-kpis" style={styles.kpis}>
            <div style={{ ...styles.kpi, ...styles.kpiDanger }}>
              <div style={styles.kpiIconDanger}>□</div>
              <div><span style={styles.kpiLabel}>Jumlah SKU Tampil</span><strong data-testid="deadstock-kpi-count" style={styles.kpiValue}>{compactNumber(visible.length)}</strong><small style={styles.kpiHelper}>periode {days} hari</small></div>
            </div>
            <div style={{ ...styles.kpi, ...styles.kpiAsset }}>
              <div style={styles.kpiIconAsset}>◎</div>
              <div><span style={styles.kpiLabel}>Nilai Aset Tampil</span><strong data-testid="deadstock-kpi-asset" style={styles.kpiValue}>{formatIDR(visibleAsset)}</strong><small style={styles.kpiHelper}>dari daftar yang dimuat</small></div>
            </div>
          </section>

          <section style={styles.listPanel}>
            <div style={styles.sectionHead}>
              <div style={styles.sectionTitleWrap}>
                <div style={styles.listIcon}>☷</div>
                <div><h2 style={styles.sectionTitle}>Daftar SKU Deadstock</h2><div style={styles.helper}>Gunakan data ini untuk keputusan promo, clearance, atau kandidat penggantian produk.</div></div>
              </div>
              <div style={styles.sectionActions}>
                <label htmlFor="deadstock-stock-filter" style={styles.filterLabel}>Tampilkan</label>
                <select id="deadstock-stock-filter" aria-label="Filter stok Deadstock" data-testid="deadstock-stock-filter"
                  style={styles.stockFilter} value={onlyAvailable ? "AVAILABLE" : "ALL"}
                  onChange={(e) => { setOnlyAvailable(e.target.value === "AVAILABLE"); setExportError(""); }}>
                  <option value="AVAILABLE">Stok Tersedia (Qty &gt; 0)</option>
                  <option value="ALL">Semua SKU Deadstock</option>
                </select>
                <span data-testid="deadstock-list-count" style={styles.count}>{compactNumber(visible.length)} SKU</span>
                <button type="button" style={styles.exportButton} data-testid="deadstock-export-xlsx"
                  onClick={exportExcel} disabled={loading || !completeResult}>
                  Export Excel (.xlsx)
                </button>
              </div>
            </div>
            {!loading && !completeResult && !error && <div style={styles.limitWarning} data-testid="deadstock-export-limit">API menyediakan {compactNumber(allItems.length)} dari {compactNumber(data.count)} SKU. Export lengkap ditahan agar data tidak terpotong.</div>}
            <div style={{ overflowX: "auto" }}>
              <table className="deadstock-table" style={styles.table}>
                <thead><tr>
                  <th>SKU</th><th>PRODUK</th><th>PENJUALAN</th><th>STOK</th><th>NILAI ASET ↑</th><th>LAST SALE</th><th>STATUS</th>
                </tr></thead>
                <tbody>
                  {loading && <tr><td colSpan={7} style={styles.empty}>Memuat data deadstock…</td></tr>}
                  {!loading && visible.map((item) => (
                      <tr key={item.product_id}>
                        <td style={styles.bold}>{item.sku_code}</td>
                        <td>
                          <div style={styles.productCell}>
                            <div><div style={styles.productName}>{item.description || "-"}</div><small style={styles.productMeta}>SKU aktif</small></div>
                          </div>
                        </td>
                        <td>{compactNumber(item.qty_period)} PCS</td>
                        <td>{compactNumber(item.stock_qty)} PCS</td>
                        <td><span style={styles.assetPill}>{formatIDR(item.stock_value)}</span></td>
                        <td>{fmtDate(item.last_sale_date)}</td>
                        <td><span style={styles.badge}><span style={styles.statusDot}>●</span>{item.status === "NEW" ? "SKU BARU" : "NO SALE"}</span></td>
                      </tr>
                  ))}
                  {!loading && visible.length === 0 && <tr><td colSpan={7} style={styles.empty}>Tidak ada SKU tanpa penjualan pada periode ini.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

const styles = {
  container: { width: "100%", maxWidth: 1460, margin: "0 auto", padding: "10px 0 42px", color: "#12203a", fontFamily: 'Arial,"Helvetica Neue",Helvetica,sans-serif' },
  header: { display: "flex", justifyContent: "space-between", gap: 20, alignItems: "center", marginBottom: 4, padding: "22px 28px", background: "#fff", border: "1px solid #e5ebf1", borderRadius: 16, boxShadow: "0 6px 20px rgba(25,45,70,.035)", flexWrap: "wrap" },
  headerMain: { display: "flex", alignItems: "center", gap: 18 },
  headerIcon: { width: 58, height: 58, borderRadius: 13, background: "#fff0f1", color: "#e11d2a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 700 },
  titleRow: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" },
  title: { margin: 0, fontSize: 31, lineHeight: 1.1, letterSpacing: "-.5px" },
  preview: { background: "#edf2f7", color: "#526176", borderRadius: 16, padding: "7px 12px", fontSize: 11, fontWeight: 800 },
  subtitle: { margin: "8px 0 0", color: "#71809a", fontSize: 14 },
  back: { padding: "12px 18px", border: "1px solid #dbe3eb", borderRadius: 10, background: "#fff", color: "#34455e", textDecoration: "none", fontWeight: 700 },
  controlPanel: { background: "#fff", border: "1px solid #e5ebf1", borderRadius: 16, padding: "22px 28px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "end", gap: 20, boxShadow: "0 6px 20px rgba(25,45,70,.035)", flexWrap: "wrap" },
  label: { color: "#65758a", fontSize: 12, fontWeight: 800, marginBottom: 8 },
  tabs: { display: "flex", gap: 7 },
  tab: { border: "1px solid #e1e7ed", background: "#f7f9fb", color: "#536279", borderRadius: 10, padding: "12px 28px", fontWeight: 700, cursor: "pointer", minWidth: 105 },
  active: { borderColor: "#ffd5d8", color: "#d92535", background: "#fff0f1", boxShadow: "none" },
  controlRight: { display: "flex", alignItems: "center", gap: 14 },
  periodHint: { color: "#7b8799", fontSize: 13 },
  refresh: { border: 0, background: "#fff0f1", color: "#d92535", borderRadius: 10, padding: "12px 18px", fontWeight: 800, cursor: "pointer" },
  kpis: { display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 14, marginBottom: 18 },
  kpi: { background: "#fff", border: "1px solid #e5ebf1", borderRadius: 16, padding: "22px 26px", display: "flex", alignItems: "center", gap: 18, minHeight: 108, boxShadow: "0 6px 20px rgba(25,45,70,.035)" },
  kpiDanger: { background: "linear-gradient(110deg,#fff 0%,#fffafb 100%)" },
  kpiAsset: { background: "linear-gradient(110deg,#fff 0%,#fbfefc 100%)" },
  kpiIconDanger: { width: 58, height: 58, borderRadius: 12, background: "#fff0f1", color: "#e11d2a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 },
  kpiIconAsset: { width: 58, height: 58, borderRadius: 12, background: "#eaf8f1", color: "#078653", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 },
  kpiLabel: { display: "block", color: "#6d7b92", fontSize: 13, fontWeight: 700 },
  kpiValue: { display: "block", color: "#17233d", fontSize: 28, lineHeight: 1.15, marginTop: 5 },
  kpiHelper: { display: "block", color: "#8491a2", fontSize: 12, marginTop: 3 },
  listPanel: { background: "#fff", border: "1px solid #e5ebf1", borderRadius: 16, padding: "20px 22px 10px", marginBottom: 14, boxShadow: "0 6px 20px rgba(25,45,70,.035)" },
  sectionHead: { display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 14, flexWrap: "wrap" },
  sectionTitleWrap: { display: "flex", alignItems: "center", gap: 14 },
  listIcon: { width: 48, height: 48, borderRadius: 11, background: "#eef3f7", color: "#52657c", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 },
  sectionTitle: { margin: 0, fontSize: 21 },
  sectionActions: { display: "flex", justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap", gap: 10 },
  filterLabel: { fontSize: 12, color: "#65758a", fontWeight: 700 },
  stockFilter: { padding: "10px 11px", borderRadius: 10, border: "1px solid #dbe3eb", background: "#fff", color: "#34455e", fontWeight: 700, maxWidth: 240 },
  exportButton: { padding: "10px 16px", border: "1px solid #dbe3eb", borderRadius: 10, background: "#eff7f2", color: "#086b44", fontWeight: 800, cursor: "pointer" },
  limitWarning: { marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: "#fff6e8", color: "#8d5e0b", fontSize: 13 },
  helper: { color: "#738197", fontSize: 13, marginTop: 4 },
  count: { background: "#f2f5f8", color: "#536279", borderRadius: 18, padding: "9px 13px", fontSize: 12, fontWeight: 700 },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 1050, fontSize: 13, color: "#40516b", fontFamily: "Verdana, Geneva, sans-serif" },
  empty: { textAlign: "center", padding: 30, color: "#8491a2" },
  bold: { fontWeight: 800, color: "#15223a" },
  productCell: { display: "flex", alignItems: "center", minWidth: 250 },
  productName: { color: "#27364f", fontSize: 14 },
  productMeta: { color: "#8995a6", fontSize: 11, display: "block", marginTop: 3 },
  assetPill: { display: "inline-block", background: "#eaf8f1", color: "#165f48", borderRadius: 11, padding: "9px 13px", fontWeight: 700 },
  badge: { display: "inline-flex", alignItems: "center", gap: 7, borderRadius: 16, padding: "7px 11px", background: "#fdebed", color: "#c21f2d", fontSize: 11, fontWeight: 800 },
  statusDot: { fontSize: 10, color: "#e11d2a" },
  error: { background: "#fff0f1", border: "1px solid #f4bfc4", color: "#b4232f", borderRadius: 10, padding: "11px 14px", marginBottom: 14 },
};

const styleTag = document.createElement("style");
styleTag.textContent = `
  .deadstock-table th,.deadstock-table td{padding:12px 10px;border-bottom:1px solid #edf0f3;text-align:left;white-space:nowrap}
  .deadstock-table thead th{background:#f5f7f9;color:#65758a;font-size:11px;font-weight:800;letter-spacing:.2px}
  .deadstock-table tbody tr:hover{background:#fafbfd}
  .deadstock-table tbody tr:last-child td{border-bottom:0}
  @media(max-width:900px){.app-shell .content{padding-left:14px!important;padding-right:14px!important}.deadstock-kpis{grid-template-columns:1fr!important}}
  @media(max-width:700px){.deadstock-container{width:calc(100% - 24px)!important}.deadstock-header{align-items:stretch!important}.deadstock-control{align-items:stretch!important}.deadstock-tabs{width:100%;overflow:auto}.deadstock-tabs button{flex:1;min-width:95px}.deadstock-control-right{justify-content:space-between}}
`;
if (!document.head.querySelector("[data-deadstock-style]")) { styleTag.setAttribute("data-deadstock-style","true"); document.head.appendChild(styleTag); }
